<template>
  <div>
    <p
      class="text-body-1 px-0 font-weight-medium"
    >
      Your Plan
    </p>
    <p class="text-caption grey--text text--darken-3 px-0 mt-n4">
      {{ humanizePlan(planId) }}
    </p>
    <template v-for="(addon, index) in addonEntries">
      <v-row
        :key="index"
        class="flex-nowrap px-3 justify-space-between align-center my-4"
      >
        <span
          class="text-body-1"
          :class="{'font-weight-bold' : addon.added, 'grey--text' : !addon.clickable}"
        >
          {{ addon.text }}
        </span>
        <div class="d-flex align-center g-4">
          <template v-if="showAddonPrices">
            <div>
              <animated-number
                class="text-right text-body-1 grey--text text--darken-4"
                prefix="$"
                :number="addon.price"
              />
              <span class="text-caption">/{{ frequency }}</span>
              <span
                v-if="showCouponDisclaimer"
                class="success--text text-caption"
              >*</span>
            </div>
          </template>
          <v-btn
            slot="append"
            icon
            class="button grey"
            :disabled="!addon.clickable"
            :class="{ 'primary': !addon.added, 'lighten-3': addon.added }"
            :color="!addon.added && addon.clickable ? 'white' : 'grey--text'"
            @click="toggle(addon.id)"
          >
            <v-icon>
              {{ addon.added ? 'mdi-minus' : 'mdi-plus' }}
            </v-icon>
          </v-btn>
        </div>
      </v-row>
      <p
        :key="`${index}-description`"
        class="text-caption grey--text text--darken-1"
      >
        {{ addon.description }}
      </p>
    </template>
    <v-divider class="my-4" />
    <v-row class="flex-nowrap px-3 justify-space-between my-2">
      <span class="font-weight-medium primary-grey--text text-body-1 px-0">
        Total
      </span>
      <span
        class="text-right text-h6 primary--text font-weight-regular pl-0"
        :class="{'pr-13': showAddonPrices}"
      >
        <animated-number
          prefix="$"
          :number="total"
        />
        <span class="text-caption">/{{ frequency }}</span>
        <span
          v-if="showCouponDisclaimer"
          class="success--text text-caption"
        >*</span>
      </span>
    </v-row>
    <v-row
      v-if="showCouponDisclaimer"
      class="flex-nowrap px-3 my-2 success--text"
    >
      <span class="text-caption">*</span>
      <span class="">Any coupons or discounts will be applied at checkout.</span>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AnimatedNumber from '@/components/generic/AnimatedNumber.vue';
import {
  WEEKLY_RESEARCH_ADDON,
  SMART_PLAN,
  ANNUAL,
  MONTHLY,
  USD,
} from './data/constants';
import { humanizePlan } from './data/helpers';

export default {
  name: 'AddonSelector',

  components: {
    AnimatedNumber,
  },

  props: {
    annualBilling: {
      type: Boolean,
      default: true,
    },

    showCouponDisclaimer: {
      type: Boolean,
      default: true,
    },

    showAddonPrices: {
      type: Boolean,
      default: false,
    },

    includePlanPrice: {
      type: Boolean,
      default: true,
    },

    value: {
      type: Object,
      default: () => {},
    },

    planId: {
      type: String,
      default: SMART_PLAN,
    },

    priceOverride: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      addons: {
        [WEEKLY_RESEARCH_ADDON]: false,
      },
      currency: USD,
    };
  },

  computed: {
    ...mapGetters('chargebee', ['getPrice']),

    total() {
      let price = this.selectedAddons.map((addonId) => this.getPrice(
        {
          id: addonId,
          frequency: this.annualBilling ? ANNUAL : MONTHLY,
          currency: this.currency,
        },
        { normalize: MONTHLY },
      )).reduce((a, b) => a + b, 0);

      if (this.includePlanPrice) {
        if (this.priceOverride) {
          return (this.annualBilling ? price * 12 : price) + this.priceOverride;
        }
        price += this.getPrice(
          {
            id: this.planId,
            frequency: this.annualBilling ? ANNUAL : MONTHLY,
            currency: this.currency,
          },
          { normalize: MONTHLY },
        );
      }
      if (this.annualBilling) {
        price *= 12;
      }
      return price;
    },

    frequency() {
      return this.annualBilling ? 'year' : 'month';
    },

    selectedAddons() {
      return Object.entries(this.addons).filter(([, value]) => value).map(([key]) => key);
    },

    addonEntries() {
      return [
        {
          id: WEEKLY_RESEARCH_ADDON,
          added: this.isAdded(WEEKLY_RESEARCH_ADDON),
          clickable: true,
          text: 'Add Weekly Research',
          description: 'The Weekly Research provided by AgResource is now available on GrainFox. The Weekly Research provides readers with information on long term fundamental trends and worldwide analysis. Using in-depth statistical research, and a detailed global outlook, the Weekly Newsletter gives an overall view of current agriculture.',
          price: this.addonPrice(WEEKLY_RESEARCH_ADDON),
        },
      ];
    },
  },

  watch: {
    value: {
      deep: true,
      handler(val) {
        this.addons = { ...this.addons, ...val };
      },
    },
  },

  methods: {
    ...mapActions('shared', ['fetchCommoditiesAndProvinces']),
    humanizePlan,

    toggle(addon) {
      this.addons[addon] = !this.addons[addon];
    },

    isAdded(addon) {
      return Boolean(this.addons[addon]);
    },

    addonPrice(addon) {
      return this.getPrice({
        id: addon,
        frequency: this.annualBilling ? ANNUAL : MONTHLY,
        currency: this.currency,
      });
    },
  },
};
</script>
